import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ExternalLink, FileText } from 'lucide-react';
import { getAuthHeaders } from '../../../contexts/AuthContext';
import { API } from '../execShared';

export default function ApplicationsSection() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postUrl, setPostUrl] = useState('');
  const [submittingId, setSubmittingId] = useState(null);
  const [draftModal, setDraftModal] = useState(null);
  const [draftText, setDraftText] = useState('');
  const [draftSubmitting, setDraftSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/applications/my`, { headers: getAuthHeaders() }).then(r => setApps(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const submitPost = async (appId) => {
    if (!postUrl) return;
    setSubmittingId(appId);
    try {
      await axios.put(`${API}/applications/${appId}/submit-post`, { post_url: postUrl }, { headers: getAuthHeaders() });
      setApps(apps.map(a => a.id === appId ? { ...a, status: 'completed', post_url: postUrl } : a));
      setPostUrl('');
    } catch {} finally { setSubmittingId(null); }
  };

  const submitDraft = async () => {
    if (!draftText.trim() || !draftModal) return;
    setDraftSubmitting(true);
    try {
      await axios.post(`${API}/submissions/create`, {
        campaign_id: draftModal.campaign_id, post_draft: draftText, platform: 'LinkedIn'
      }, { headers: getAuthHeaders() });
      setDraftModal(null);
      setDraftText('');
      alert('Draft submitted for AI review. You will be notified of the result.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Submission failed');
    } finally { setDraftSubmitting(false); }
  };

  const statusColors = { applied: 'text-[#FF4343]', reviewing: 'text-blue-400', accepted: 'text-[#10B981]', rejected: 'text-red-400', completed: 'text-[#10B981]' };

  return (
    <div className="space-y-4" data-testid="applications-section">
      <h2 className="font-display text-2xl font-bold">My <span className="text-[#FF4343]">Applications</span></h2>
      {loading ? <p className="text-[#9CA3AF]">Loading...</p> : apps.length === 0 ? (
        <div className="text-center py-12 bg-[#111827] border border-white/10">
          <FileText className="w-10 h-10 text-[#6B7280] mx-auto mb-3" />
          <p className="text-[#9CA3AF]">No applications yet.</p>
          <Link to="/campaigns" className="text-[#FF4343] text-sm hover:underline mt-2 inline-block">Browse Campaigns</Link>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="applications-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Campaign</th>
                <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Brand</th>
                <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Status</th>
                <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Compensation</th>
                <th className="text-left py-3 text-[#6B7280] font-mono text-xs uppercase">Action</th>
              </tr>
            </thead>
            <tbody>
              {apps.map(a => (
                <tr key={a.id} className="border-b border-white/5">
                  <td className="py-3 text-white">{a.campaign_title}</td>
                  <td className="py-3 text-[#9CA3AF]">{a.brand_name}</td>
                  <td className="py-3"><span className={`font-mono text-xs uppercase ${statusColors[a.status] || 'text-[#6B7280]'}`}>{a.status}</span></td>
                  <td className="py-3 font-mono text-[#FF4343]">{a.compensation}</td>
                  <td className="py-3">
                    {a.status === 'accepted' && !a.post_url ? (
                      <div className="flex gap-2 flex-wrap">
                        <input value={submittingId === a.id ? postUrl : ''} onChange={e => { setPostUrl(e.target.value); setSubmittingId(a.id); }}
                          placeholder="Post URL" className="px-2 py-1 bg-white/5 border border-white/10 text-white text-xs w-40 focus:outline-none focus:border-[#FF4343]" />
                        <button onClick={() => submitPost(a.id)} data-testid={`submit-post-${a.id}`}
                          className="text-xs px-3 py-1 border border-[#FF4343] text-[#FF4343] hover:bg-[#FF4343] hover:text-[#090E33] transition-colors">
                          Submit
                        </button>
                        <button onClick={() => setDraftModal(a)} data-testid={`submit-draft-${a.id}`}
                          className="text-xs px-3 py-1 border border-white/20 text-[#9CA3AF] hover:text-white transition-colors">
                          Submit Draft
                        </button>
                      </div>
                    ) : a.post_url ? (
                      <a href={a.post_url} target="_blank" rel="noopener noreferrer" className="text-[#FF4343] text-xs flex items-center gap-1 hover:underline">
                        View Post <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <Link to={`/campaigns/${a.campaign_id}`} className="text-[#9CA3AF] text-xs hover:text-white">View Brief</Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Draft Submission Modal */}
      {draftModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDraftModal(null)} />
          <div className="relative glass max-w-lg w-full p-6" data-testid="draft-submission-modal">
            <h3 className="text-sm font-semibold mb-2">Submit Post Draft for Review</h3>
            <p className="text-xs text-[#6B7280] mb-4">Campaign: {draftModal.campaign_title}</p>
            <textarea value={draftText} onChange={e => setDraftText(e.target.value)} rows={8}
              placeholder="Write your post draft here. Include any hashtags, mentions, and disclosure language..."
              data-testid="draft-textarea"
              className="w-full px-3 py-2 bg-[#090E33] border border-white/10 text-white text-sm placeholder-[#6B7280] focus:outline-none focus:border-[#FF4343] resize-none mb-4" />
            <div className="flex gap-2">
              <button onClick={submitDraft} disabled={draftSubmitting || !draftText.trim()} data-testid="submit-draft-btn"
                className="border-2 border-[#FF4343] bg-[#FF4343] text-[#090E33] hover:bg-[#E03030] px-5 py-2 text-sm font-semibold transition-all disabled:opacity-50">
                {draftSubmitting ? 'Submitting...' : 'Submit for AI Review'}
              </button>
              <button onClick={() => setDraftModal(null)} className="border border-white/20 text-[#9CA3AF] hover:text-white px-5 py-2 text-sm transition-colors">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== MY CAMPAIGNS (Post Submission Flow) ====================
